import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './TechnicalService.scss';

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const STATUS_META = {
  7: { label: 'Muhasebeye Aktarildi', className: 'accounting' }
};
const INVOICED_DRAFT_STORAGE_KEY = 'accountingInvoicedDrafts';
const TECHNICAL_SERVICE_ARCHIVE_CACHE_KEY = 'technicalServiceArchiveCacheV1';
const ARCHIVE_PAGE_SIZE = 10;

const readArchiveCache = () => {
  try {
    const raw = sessionStorage.getItem(TECHNICAL_SERVICE_ARCHIVE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeArchiveCache = (payload) => {
  try {
    sessionStorage.setItem(TECHNICAL_SERVICE_ARCHIVE_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore cache failures
  }
};

const readInvoicedDrafts = () => {
  try {
    return JSON.parse(localStorage.getItem(INVOICED_DRAFT_STORAGE_KEY) || '{}');
  } catch (error) {
    console.error('Kesilen fatura kayitlari okunamadi:', error);
    return {};
  }
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }

  return 1;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (Array.isArray(responseData?.messages) && responseData.messages.length > 0) {
    return responseData.messages.join(' ');
  }

  if (responseData?.message) {
    return responseData.message;
  }

  return fallbackMessage;
};

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(numericValue);
};

const formatDate = (value) => {
  if (!value) return '-';

  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const getUploadUrl = (fileName) => {
  if (!fileName) return '#';

  if (window.location.hostname === 'localhost') {
    return `http://localhost:5058/uploads/${fileName}`;
  }

  const protocol = window.location.protocol;
  const domain = process.env.REACT_APP_API_BASE_URL.replace(/^https?:\/\//, '');
  return `${protocol}//${domain}/uploads/${fileName}`;
};

const TechnicalServiceArchivePage = () => {
  const initialCache = readArchiveCache();
  const [requests, setRequests] = useState(initialCache?.requests || []);
  const [loading, setLoading] = useState(!(initialCache?.requests?.length > 0));
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(initialCache?.selectedRequestId || null);
  const [selectedRequestDetail, setSelectedRequestDetail] = useState(initialCache?.selectedRequestDetail || null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [archivePage, setArchivePage] = useState(1);
  const [invoicedDrafts] = useState(() => readInvoicedDrafts());
  const [filters, setFilters] = useState({
    company: '',
    machine: '',
    dateFrom: '',
    dateTo: ''
  });

  const fetchRequests = async () => {
    const response = await api.get('/TechnicalService/requests');
    const items = response?.data?.data || [];
    setRequests(items);
    writeArchiveCache({
      requests: items,
      selectedRequestId,
      selectedRequestDetail
    });
    return items;
  };

  const openRequestDetail = async (requestId, fallbackItem = null) => {
    const listItem = fallbackItem || requests.find((item) => item.id === requestId) || null;

    try {
      setDetailLoading(true);
      setSelectedRequestId(requestId);
      setSelectedRequestDetail(listItem);
      setIsDetailModalOpen(true);

      const response = await api.get(`/TechnicalService/requests/${requestId}`);
      setSelectedRequestDetail({
        ...(response?.data?.data || listItem || {}),
        invoiceRecord: listItem?.invoiceRecord || null
      });
      writeArchiveCache({
        requests,
        selectedRequestId: requestId,
        selectedRequestDetail: {
          ...(response?.data?.data || listItem || {}),
          invoiceRecord: listItem?.invoiceRecord || null
        }
      });
      setErrorMessage('');
    } catch (error) {
      console.error('Arsiv talep detayi alinamadi:', error);

      if (!listItem) {
        setSelectedRequestDetail(null);
      }

      setErrorMessage(getApiErrorMessage(error, 'Arsiv detayi alinamadi.'));
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (requests.length === 0) {
          setLoading(true);
        }
        await fetchRequests();
      } catch (error) {
        console.error('Teknik servis arsivi alinamadi:', error);
        setErrorMessage(getApiErrorMessage(error, 'Teknik servis arsivi yuklenemedi.'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    writeArchiveCache({
      requests,
      selectedRequestId,
      selectedRequestDetail
    });
  }, [requests, selectedRequestId, selectedRequestDetail]);

  const archiveRows = useMemo(() => {
    const normalizedCompany = filters.company.trim().toLowerCase();
    const normalizedMachine = filters.machine.trim().toLowerCase();
    const normalizedDateFrom = filters.dateFrom;
    const normalizedDateTo = filters.dateTo;

    return requests
      .map((request) => {
        const normalizedStatus = normalizeStatus(request.status);
        const machineLabel =
          request.externalMachineModel ||
          request.towerCraneSerialNo ||
          request.externalMachineName ||
          request.externalSerialNo ||
          '-';

        const completionDate =
          (request.workLogs || [])
            .filter((workLog) => workLog.isCompleted)
            .map((workLog) => workLog.processDate)
            .sort()
            .slice(-1)[0] || null;

        const completedByFromLogs =
          (request.workLogs || [])
            .filter((workLog) => workLog.isCompleted)
            .map((workLog) => workLog.userName)
            .filter(Boolean)
            .join(', ');

        const completedByFromAssignments =
          (request.assignments || [])
            .map((assignment) => assignment.userName || assignment.assignedUserName)
            .filter(Boolean)
            .join(', ');

        const assignedTeam =
          (request.assignments || [])
            .map((assignment) => assignment.userName || assignment.assignedUserName)
            .filter(Boolean)
            .join(', ') || '-';

        const completedBy =
          completedByFromLogs ||
          request.completedByUserName ||
          request.completedBy ||
          completedByFromAssignments ||
          '-';

        const invoiceRecord =
          request.isInvoiceCut
            ? {
                invoiceNumber: request.invoiceNumber || '',
                ettn: request.invoiceEttn || '',
                sentAt: request.invoiceCutDate || null
              }
            : invoicedDrafts[`technical-${request.id}`] || null;
        const archiveStatusLabel = invoiceRecord ? 'Fatura Kesildi' : 'Fatura Bekliyor';
        const archiveStatusClass = invoiceRecord ? 'success' : STATUS_META[7].className;

        return {
          ...request,
          normalizedStatus,
          machineLabel,
          completionDate,
          completedBy,
          invoiceRecord,
          archiveStatusLabel,
          archiveStatusClass
        };
      })
      .filter((request) => request.normalizedStatus === 7)
      .filter((request) => {
        const companyMatch =
          !normalizedCompany ||
          (request.requestingCompanyName || '').toLowerCase().includes(normalizedCompany);

        const machineMatch =
          !normalizedMachine ||
          (request.machineLabel || '').toLowerCase().includes(normalizedMachine);

        const baseDateValue = request.completionDate || request.requestedDate;
        const baseDate = baseDateValue ? new Date(baseDateValue).toISOString().slice(0, 10) : '';
        const fromMatch = !normalizedDateFrom || (baseDate && baseDate >= normalizedDateFrom);
        const toMatch = !normalizedDateTo || (baseDate && baseDate <= normalizedDateTo);

        return companyMatch && machineMatch && fromMatch && toMatch;
      });
  }, [filters, invoicedDrafts, requests]);

  const selectedArchiveRequest =
    archiveRows.find((request) => request.id === selectedRequestId) || null;
  const archivePageCount = Math.max(1, Math.ceil(archiveRows.length / ARCHIVE_PAGE_SIZE));
  const paginatedArchiveRows = useMemo(() => {
    const startIndex = (archivePage - 1) * ARCHIVE_PAGE_SIZE;
    return archiveRows.slice(startIndex, startIndex + ARCHIVE_PAGE_SIZE);
  }, [archivePage, archiveRows]);

  useEffect(() => {
    setArchivePage(1);
  }, [filters.company, filters.dateFrom, filters.dateTo, filters.machine]);

  useEffect(() => {
    if (archivePage > archivePageCount) {
      setArchivePage(archivePageCount);
    }
  }, [archivePage, archivePageCount]);

  const completedWorkLogs = useMemo(
    () => (selectedRequestDetail?.workLogs || []).filter((workLog) => workLog.isCompleted),
    [selectedRequestDetail]
  );

  const allUsedParts = useMemo(
    () => completedWorkLogs.flatMap((workLog) => workLog.usedParts || []),
    [completedWorkLogs]
  );

  return (
    <div className="technical-service-page archive-page">
      <Navbar />
      <div className="technical-service-container">
        <div className="technical-service-header">
          <div>
            <h1>Arsiv</h1>
          </div>
        </div>

        {errorMessage && <div className="technical-service-alert error">{errorMessage}</div>}

        <section className="technical-service-card archive-list-card">
          <div className="archive-filter-grid">
            <label>
              Firma Ara
              <input
                type="text"
                value={filters.company}
                onChange={(event) => setFilters((prev) => ({ ...prev, company: event.target.value }))}
                placeholder="Firma adina gore ara"
              />
            </label>
            <label>
              Makine Modeli Ara
              <input
                type="text"
                value={filters.machine}
                onChange={(event) => setFilters((prev) => ({ ...prev, machine: event.target.value }))}
                placeholder="Makine / model ara"
              />
            </label>
            <label>
              Baslangic Tarihi
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
              />
            </label>
            <label>
              Bitis Tarihi
              <input
                type="date"
                value={filters.dateTo}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              />
            </label>
          </div>

          <h2>Tamamlanan Talepler</h2>
          {loading ? (
            <p>Arsiv yukleniyor...</p>
          ) : archiveRows.length > 0 ? (
            <div className="request-stack">
              {paginatedArchiveRows.map((request) => (
                <button
                  key={request.id}
                  type="button"
                  className={`request-panel request-button archive-request-card ${selectedArchiveRequest?.id === request.id ? 'active' : ''}`}
                  onClick={() => openRequestDetail(request.id, request)}
                >
                  <div className="request-panel-header">
                    <div className="archive-request-main">
                      <h3>{request.requestNo || `Talep #${request.id}`}</h3>
                      <p>{request.requestingCompanyName} - {request.subject}</p>
                    </div>
                    <div className="archive-request-side">
                      <div className="archive-request-meta">
                        <div><strong>Makine:</strong> {request.machineLabel}</div>
                        <div><strong>Formu Tamamlayan:</strong> {request.completedBy}</div>
                        <div><strong>Tutar:</strong> {formatCurrency(request.totalPrice)}</div>
                        <div><strong>Konum:</strong> {request.workSiteLocation || '-'}</div>
                        <div><strong>Operasyon:</strong> {formatDate(request.operationDate)}</div>
                        <div><strong>Tamamlanma:</strong> {formatDate(request.completionDate || request.requestedDate)}</div>
                      </div>
                      <span className={`badge ${request.archiveStatusClass}`}>
                        {request.archiveStatusLabel}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
              {archiveRows.length > ARCHIVE_PAGE_SIZE && (
                <div className="archive-pagination">
                  <button type="button" onClick={() => setArchivePage((page) => Math.max(1, page - 1))} disabled={archivePage === 1}>
                    ‹
                  </button>
                  <span>{archivePage} / {archivePageCount}</span>
                  <button type="button" onClick={() => setArchivePage((page) => Math.min(archivePageCount, page + 1))} disabled={archivePage === archivePageCount}>
                    ›
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p>Filtrelere uyan arsiv kaydi bulunamadi.</p>
          )}
        </section>

        {isDetailModalOpen && (
          <div className="archive-modal-backdrop" onClick={() => setIsDetailModalOpen(false)}>
            <section
              className="technical-service-card archive-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="archive-modal-header">
                <h2>Arşiv Detayı</h2>
                <button
                  type="button"
                  className="archive-modal-close"
                  onClick={() => setIsDetailModalOpen(false)}
                  aria-label="Detay penceresini kapat"
                >
                  X
                </button>
              </div>

              {detailLoading ? (
                <p>Arşiv detayı yükleniyor...</p>
              ) : selectedRequestDetail ? (
                <div className="approval-panel">
                  <div className="job-summary">
                    <div><strong>Talep No:</strong> {selectedRequestDetail.requestNo}</div>
                    <div><strong>Firma:</strong> {selectedRequestDetail.requestingCompanyName}</div>
                    <div><strong>Makine:</strong> {selectedRequestDetail.externalMachineModel || selectedRequestDetail.towerCraneSerialNo || selectedRequestDetail.externalMachineName || '-'}</div>
                    <div><strong>Şantiye:</strong> {selectedRequestDetail.workSiteName || selectedRequestDetail.externalWorkSiteName || '-'}</div>
                    <div><strong>Konum:</strong> {selectedRequestDetail.workSiteLocation || '-'}</div>
                    <div><strong>Operasyon:</strong> {formatDate(selectedRequestDetail.operationDate)}</div>
                    <div><strong>Formu Tamamlayan:</strong> {completedWorkLogs.map((workLog) => workLog.userName).filter(Boolean).join(', ') || '-'}</div>
                    <div><strong>Görevli Ekip:</strong> {(selectedRequestDetail.assignments || []).map((assignment) => assignment.userName || assignment.assignedUserName).filter(Boolean).join(', ') || '-'}</div>
                    <div><strong>Toplam Tutar:</strong> {formatCurrency(selectedRequestDetail.totalPrice)}</div>
                    <div><strong>Durum:</strong> {selectedRequestDetail.invoiceRecord ? 'Fatura Kesildi' : 'Fatura Bekliyor'}</div>
                    {selectedRequestDetail.invoiceRecord && (
                      <>
                        <div><strong>Fatura:</strong> Fatura Kesildi</div>
                        <div><strong>Belge No:</strong> {selectedRequestDetail.invoiceRecord.invoiceNumber || '-'}</div>
                        <div><strong>ETTN:</strong> {selectedRequestDetail.invoiceRecord.ettn || '-'}</div>
                      </>
                    )}
                  </div>

                  <div className="approval-worklogs">
                    {completedWorkLogs.map((workLog) => (
                      <div key={workLog.id} className="approval-worklog-card">
                        <div className="approval-worklog-header">
                          <strong>{workLog.userName}</strong>
                          <span>{formatDate(workLog.processDate)}</span>
                        </div>
                        <p>{workLog.description || 'Aciklama girilmedi.'}</p>
                        {workLog.files?.length > 0 && (
                          <div className="file-pill-row">
                            {workLog.files.map((file) => (
                              <a
                                key={file.id}
                                className="file-pill"
                                href={getUploadUrl(file.fileName)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {file.fileName}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="parts-section manager-parts-section">
                    <div className="parts-header">
                      <h3>Kullanilan Kalemler</h3>
                      <strong>{formatCurrency(selectedRequestDetail.totalPrice)}</strong>
                    </div>

                    {allUsedParts.length > 0 ? (
                      allUsedParts.map((part) => (
                        <div key={part.id} className="parts-row manager-parts-row archive-parts-row">
                          <div className="part-readonly-box">
                            <strong>{part.itemName}</strong>
                            <span>{Number(part.quantity || 0)} {part.unit || ''}</span>
                          </div>
                          <div className="part-total-box">
                            <span>Birim Fiyat</span>
                            <strong>{formatCurrency(part.unitPrice)}</strong>
                          </div>
                          <div className="part-total-box">
                            <span>Kalem Toplami</span>
                            <strong>{formatCurrency(Number(part.quantity || 0) * Number(part.unitPrice || 0))}</strong>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p>Bu arsiv kaydi icin kalem bulunmuyor.</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="empty-form-state compact">
                  <h3>Kayit secimi bekleniyor</h3>
                  <p>Arsiv kartlarindan birini sec. Detay, fotograflar ve girilen tutarlar burada gorunur.</p>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicalServiceArchivePage;
